import {
  canReadManagementWorkspace,
  canReadProperties,
  readCurrentAuthorization,
} from '@/api/core/authorization';
import { readRoomOnlineAccess } from '@/api/core/room-online-access';
import { isApiError } from '@/api/http/errors';
import { isUuid } from '@/lib/validation';
import type { PropertyRuntime } from '../property/property-queries';

/** A legacy catalog rejection can be narrower than the current Property grant. */
export async function readRoomAccessWithPermissionCheck(
  runtime: PropertyRuntime,
  generation: number,
  propertyId: string,
  roomId: string,
  signal: AbortSignal,
  isCurrent: () => boolean,
) {
  const initial = runtime.session.getSnapshot();
  const current = () => {
    const now = runtime.session.getSnapshot();
    return (
      !signal.aborted &&
      isCurrent() &&
      now.status === 'authorized' &&
      now.generation === generation &&
      !now.deniedDomains.includes('property')
    );
  };
  const actor = async () => {
    const value = await runtime.client.get<unknown>('users/me', { signal });
    if (!value || typeof value !== 'object' || !('user_id' in value) || !isUuid(value.user_id))
      throw new Error('현재 작업자를 확인하지 못했습니다.');
    return value.user_id;
  };
  let originalActor: string | undefined;
  try {
    if (current()) originalActor = await actor();
    return await readRoomOnlineAccess(runtime.client, propertyId, roomId, signal);
  } catch (error) {
    if (current() && isApiError(error) && error.status === 401)
      runtime.session.reportAccessDenied(generation);
    if (!current() || !isApiError(error) || error.status !== 403) throw error;
    if (!originalActor) {
      runtime.session.reportDomainAccessDenied('property', generation);
      throw error;
    }
    try {
      const before = await actor();
      if (!current()) throw error;
      const authorization = await readCurrentAuthorization(runtime.client, signal);
      if (!current()) throw error;
      const after = await actor();
      if (!current()) throw error;
      if (
        before !== originalActor ||
        after !== originalActor ||
        !canReadManagementWorkspace(authorization)
      ) {
        runtime.session.reportAccessDenied(generation);
      } else if (!canReadProperties(authorization)) {
        runtime.session.reportDomainAccessDenied('property', generation);
      } else if (JSON.stringify(authorization) !== JSON.stringify(initial.authorization)) {
        // Publish changed permissions through the existing global reauthorization path.
        await runtime.session.loadCurrentAuthorization();
      }
      // Only an unchanged current actor and freshly confirmed identical grant keep this local.
    } catch (failure) {
      if (current()) {
        if (isApiError(failure) && failure.status === 401)
          runtime.session.reportAccessDenied(generation);
        else runtime.session.reportDomainAccessDenied('property', generation);
      }
    }
    throw error;
  }
}
